<template>
  <section class="vh-shell mb-4">
    <header class="vh-head">
      <div class="vh-head__main">
        <div class="vh-eyebrow">
          <span class="vh-dot"></span>
          <span class="brand-mark">WaizmannVergleich</span>&nbsp;/&nbsp;ERGEBNIS
        </div>
        <h1 class="vh-title">
          Ihr Ergebnis:
          <span class="vh-title__sub" :class="berechnung.fontClass">
            {{ berechnung.show_ber_geschlecht }},
            {{ berechnung.show_ber_geburtstag }}
          </span>
        </h1>
        <p class="vh-sub">
          {{ berechnung.showBerechnungName }}en &middot; sortiert nach höchster
          Leistung &amp; Preis-Leistung
        </p>
      </div>
      <div class="vh-head__share">
        <button
          type="button"
          class="vh-share"
          @click="berechnungStore.setShowPop(false, 'berechnungteilen')"
        >
          <v-icon icon="mdi-share-variant-outline" size="16" class="mr-1" />
          Ergebnis teilen
        </button>
        <div class="vh-share__hint">Verschicken Sie das Ergebnis per E-Mail.</div>
      </div>
    </header>

    <div class="vh-grid">
      <div class="vh-card">
        <div class="vh-card__label">Berechnungsvorgaben</div>
        <ul class="vh-list">
          <li
            v-for="(item, index) in berechnung.berlog"
            :key="index"
            v-html="nl2br(item.value)"
          ></li>
        </ul>
        <router-link
          class="vh-link mt-1"
          :to="`/vergleich/${berechnung.showLinkStarten}`"
        >
          Vorgaben anpassen →
        </router-link>
      </div>

      <div class="vh-card">
        <div class="vh-card__label">Antrag-AnnahmeCheck</div>
        <div class="vh-stat">
          <span class="vh-stat__num text-pos">
            {{ vergleichCount.tarifeCount }}
          </span>
          <span class="vh-stat__txt">Tarife sind beantragbar</span>
        </div>
        <a
          class="vh-link"
          @click="berechnungStore.setShowPop(false, 'annahmekriterien')"
        >
          berücksichtigte Kriterien →
        </a>

        <div
          v-if="vergleichCount.notarifeCount == 0"
          class="vh-note vh-note--ok"
        >
          <v-icon icon="mdi-check-circle-outline" size="16" class="mr-1" />
          Alle getesteten Tarife können beantragt werden.
        </div>

        <div
          v-if="vergleichCount.notarifeCount > 0"
          class="vh-note vh-note--warn"
        >
          <v-icon icon="mdi-alert-circle-outline" size="16" class="mr-1" />
          <span v-if="vergleichCount.notarifeCount > 1">
            <strong>{{ vergleichCount.notarifeCount }} Tarife</strong>
            würden Ihren Antrag ablehnen.
          </span>
          <span v-else>
            <strong>1 Tarif</strong> würde Ihren Antrag ablehnen.
          </span>
          <br />
          <a
            class="vh-link"
            @click="berechnungStore.setShowPop(false, 'annahmecheck')"
          >
            warum manche Tarife nicht abschließbar sind →
          </a>
        </div>
      </div>

      <div class="vh-card vh-card--clear" v-if="berechnung.ber_segment <= 1">
        <ClearmentHeader />
      </div>
    </div>

    <div class="vh-actions">
      <v-btn
        size="small"
        :color="berechnung.fontColorVue"
        variant="flat"
        rounded="0"
        class="vh-btn"
        @click="berechnungStore.setShowPop(false, 'schnellvergleich')"
      >
        <v-icon icon="mdi-table" size="16" class="mr-1" />
        Schnellübersicht
      </v-btn>
      <v-btn
        v-if="berechnung.ber_segment <= 1 && berechnung.ber_algo_view != 2"
        size="small"
        :color="berechnung.fontColorVue"
        variant="flat"
        rounded="0"
        class="vh-btn"
        @click="berechnungStore.setShowPop(false, 'vergleichhightech')"
      >
        <v-icon icon="mdi-chip" size="16" class="mr-1" />
        Hightech-Leistungen
      </v-btn>
      <v-btn
        v-if="berechnung.ber_segment <= 1 && berechnung.ber_algo_view == 2"
        size="small"
        :color="berechnung.fontColorVue"
        variant="flat"
        rounded="0"
        class="vh-btn"
        @click="berechnungStore.setShowPop(false, 'vergleichkinder')"
      >
        <v-icon icon="mdi-baby-face-outline" size="16" class="mr-1" />
        Kinder-Leistungen
      </v-btn>
      <v-btn
        size="small"
        :color="berechnung.fontColorVue"
        variant="flat"
        rounded="0"
        class="vh-btn"
        @click="berechnungStore.setShowPop(false, 'beitragsentwicklung')"
      >
        <v-icon icon="mdi-chart-line" size="16" class="mr-1" />
        Beitragsentwicklung
      </v-btn>
      <v-btn
        v-for="sv in berechnung.spezialVergleiche"
        :key="sv.text"
        size="small"
        :color="berechnung.fontColorVue"
        variant="flat"
        rounded="0"
        class="vh-btn"
        @click="berechnungStore.setShowPop(false, sv.pop)"
      >
        {{ sv.text }}
      </v-btn>
    </div>

    <div v-if="berechnung.berechnungHinweise" class="vh-hints">
      <div class="vh-hints__title">
        <v-icon icon="mdi-alert-outline" size="16" class="mr-1" color="error" />
        Wichtige Hinweise
      </div>
      <div class="vh-hints__items">
        <a
          v-for="h in berechnung.berechnungHinweise"
          :key="h.id"
          class="vh-link"
          @click="berechnungStore.setShowPop(false, h.pop)"
        >
          {{ h.title }}
        </a>
      </div>
      <div v-if="berechnung.ber_zb_3 == 1" class="vh-hints__paro">
        <strong>Wichtig bei Parodontose:</strong>
        <span class="text-pos"> positive Erstattungspraxis</span>.
        Falls die Behandlung abgeschlossen wurde, wählen Sie unbedingt einen
        Tarif mit „positiver" Parodontose-Erstattungspraxis.
        <a
          class="vh-link"
          @click="berechnungStore.setShowPop(false, 'paroempfehlung')"
        >
          Mehr dazu →
        </a>
      </div>
    </div>
  </section>
</template>

<script setup>
  import { defineProps } from "vue";

  import ClearmentHeader from "@/components/tarif/vergleich/ClearmentHeader";
  import { useBerechnungStore } from "@/stores/berechnung";
  import { useNl2Br } from "@/composable/useNl2Br";
  const { nl2br } = useNl2Br();
  const berechnungStore = useBerechnungStore();

  defineProps({
    berechnung: { type: Object, default: () => ({}) },
    vergleichCount: { type: Object, default: () => ({}) },
  });
</script>

<style scoped>
  .vh-shell {
    background: #ffffff;
    border: 1px solid #d8dde5;
    border-left: 3px solid #0f4c81;
    border-radius: 0;
    padding: 16px 18px;
    max-width: 999px;
  }

  .vh-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    padding-bottom: 12px;
    border-bottom: 1px solid #eef1f6;
  }
  .vh-head__main {
    flex: 1 1 auto;
    min-width: 0;
  }
  .vh-eyebrow {
    display: inline-flex;
    align-items: center;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.8px;
    color: #5b6473;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
      monospace;
    margin-bottom: 6px;
  }
  .vh-dot {
    display: inline-block;
    width: 6px;
    height: 6px;
    background: #0f4c81;
    border-radius: 0;
    margin-right: 8px;
  }
  .vh-title {
    font-size: clamp(18px, 2.6vw, 22px);
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.2px;
    margin: 0 0 4px;
  }
  .vh-title__sub {
    font-weight: 600;
    color: #0f4c81;
  }
  .vh-sub {
    font-size: 13px;
    color: #4b5563;
    margin: 0;
  }

  .vh-head__share {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
    text-align: right;
  }
  .vh-share {
    display: inline-flex;
    align-items: center;
    background: #f7f9fc;
    border: 1px solid #d8dde5;
    color: #0f4c81;
    font-weight: 600;
    font-size: 13px;
    letter-spacing: 0.2px;
    border-radius: 0;
    padding: 6px 10px;
    cursor: pointer;
  }
  .vh-share:hover {
    background: #f0f5fa;
    border-color: #0f4c81;
  }
  .vh-share__hint {
    font-size: 11px;
    color: #6b7280;
  }

  .vh-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 14px 0;
  }
  @media (min-width: 720px) {
    .vh-grid {
      grid-template-columns: 1fr 1fr 0.9fr;
    }
  }

  .vh-card {
    background: #f7f9fc;
    border: 1px solid #e5e9f0;
    border-radius: 0;
    padding: 12px 14px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .vh-card--clear {
    background: #fff;
    padding: 8px;
  }

  .vh-card__label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: #6b7280;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
      monospace;
    margin-bottom: 4px;
  }

  .vh-list {
    list-style: none;
    padding: 0;
    margin: 0;
    font-size: 13px;
    color: #1f2937;
  }
  .vh-list li {
    padding: 1px 0;
  }
  .vh-list li :deep(br) {
    display: none;
  }

  .vh-link {
    font-size: 12.5px;
    color: #0f4c81;
    cursor: pointer;
    text-decoration: none;
    font-weight: 600;
  }
  .vh-link:hover {
    text-decoration: underline;
  }

  .vh-stat {
    display: flex;
    align-items: baseline;
    gap: 6px;
    margin: 2px 0;
  }
  .vh-stat__num {
    font-size: 22px;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.4px;
  }
  .vh-stat__txt {
    font-size: 13px;
    color: #1f2937;
  }

  .vh-note {
    display: flex;
    align-items: flex-start;
    gap: 4px;
    margin-top: 6px;
    font-size: 12.5px;
    padding: 8px 10px;
    border-radius: 0;
    line-height: 1.4;
  }
  .vh-note--ok {
    background: #eef5ec;
    color: #2e7d32;
    border: 1px solid #c8e3c1;
  }
  .vh-note--warn {
    background: #fff5f0;
    color: #b3261e;
    border: 1px solid #f5c4ba;
  }

  .text-pos {
    color: #2e7d32;
  }

  .vh-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 12px 0;
    border-top: 1px solid #eef1f6;
    border-bottom: 1px solid #eef1f6;
  }
  .vh-btn {
    border-radius: 0 !important;
    letter-spacing: 0.2px;
    font-weight: 600;
    text-transform: none;
    height: 32px !important;
    padding: 0 10px !important;
  }

  .vh-hints {
    padding-top: 12px;
  }
  .vh-hints__title {
    display: inline-flex;
    align-items: center;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: #b3261e;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
      monospace;
    margin-bottom: 6px;
  }
  .vh-hints__items {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 14px;
    font-size: 13.5px;
  }
  .vh-hints__paro {
    margin-top: 8px;
    font-size: 13px;
    color: #1f2937;
    background: #fffbe6;
    border: 1px solid #ffe58f;
    border-left: 3px solid #f6a800;
    padding: 8px 10px;
    border-radius: 0;
  }
</style>
